import {
	ISO_8601,
	parseZone,
	tz,
	utc
} from "moment-timezone";

import type { JsonSchemaDefinition } from "./types";

function inputFormat(schema: JsonSchemaDefinition): string {
	return schema.format === "date" || schema.metadata?.dateOnly
		? "YYYY-MM-DD"
		: "YYYY-MM-DDTHH:mm";
}

function effectiveTimeZone(schema: JsonSchemaDefinition, timeZone?: string): string | undefined {
	if(schema.metadata?.timeZoneMode === "local") {
		return undefined;
	}

	return timeZone && tz.zone(timeZone) ? timeZone : undefined;
}

/** Convert a stored timestamp to the wall date/time displayed by the control. */
export function dateInputValue(
	value: unknown,
	schema: JsonSchemaDefinition,
	timeZone?: string,
	preserveOffset = false
): string {
	if(typeof value !== "string") {
		return "";
	}

	if(schema.format === "date") {
		return utc(value, "YYYY-MM-DD", true)
			.isValid() ? value : "";
	}

	const zone = effectiveTimeZone(schema, timeZone);
	const date = preserveOffset && !zone
		? parseZone(value, ISO_8601, true)
		: utc(value, ISO_8601, true)
			.local();

	if(zone) {
		date.tz(zone);
	}

	if(schema.metadata?.dateOnly && schema.metadata.valueTime === "end") {
		date.subtract(1, "day");
	}

	return date.isValid() ? date.format(inputFormat(schema)) : "";
}

/** Format a table summary without changing the stored value or its effective timezone. */
export function dateDisplayValue(
	value: unknown,
	schema: JsonSchemaDefinition,
	timeZone?: string,
	preserveOffset = false,
	is24Hour = true
): string {
	const input = dateInputValue(value, schema, timeZone, preserveOffset);

	if(!input || schema.format === "date" || schema.metadata?.dateOnly) {
		return input;
	}

	return utc(input, "YYYY-MM-DDTHH:mm", true).format(is24Hour ? "YYYY-MM-DD HH:mm" : "YYYY-MM-DD hh:mm A");
}

/** Date-only boundaries are calendar operations in the selected timezone, including DST. */
export function dateStoredValue(input: string, schema: JsonSchemaDefinition, timeZone?: string): string | undefined {
	const format = inputFormat(schema);
	const zone = effectiveTimeZone(schema, timeZone);
	const date = zone
		? tz(input, format, true, zone)
		: utc(input, format, true)
			.local(true);

	if(!date.isValid()) {
		return undefined;
	}

	if(schema.format === "date") {
		return date.format("YYYY-MM-DD");
	}

	if(schema.metadata?.dateOnly) {
		if(schema.metadata.valueTime === "end") {
			date.add(1, "day");
		}

		if(schema.metadata.valueTime === "middle") {
			date.hour(12);
		}
	}

	return date.format("YYYY-MM-DDTHH:mm:ssZ");
}
